import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import GlobalHeader from "../../../components/GlobalHeader";
import { LoadingButton } from "@mui/lab";

import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffect, useMemo, useState } from "react";
import { isEmail } from "validator";
import useSnack from "../../../hooks/useSnack";
import SnackAlert from "../../../components/SnackAlert";
import useApi from "../../../hooks/useApi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const roleLabel = { artista: "Artista", productor: "Difusor", admin: "Admin" };

const BoletinAdmin = () => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [destinatarios, setDestinatarios] = useState([]); // usernames/emails seleccionados
  const [manualEmail, setManualEmail] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const { showSnack, openSB, typeSB, msgSB } = useSnack();
  const { loadApi, loadingApi } = useApi();

  useEffect(() => {
    loadApi("admin/users", true, "get")
      .then((res) => setUsuarios(res.data || []))
      .catch(() => {});
  }, []);

  const opciones = useMemo(
    () =>
      usuarios
        .filter((u) => u.username)
        .map((u) => ({
          email: u.username,
          label: `${u.name || "Sin nombre"} — ${u.username} (${
            roleLabel[u.role] || u.role
          })`,
          role: u.role,
        })),
    [usuarios]
  );

  const agregarGrupo = (role) => {
    const emailsGrupo = usuarios
      .filter((u) => (role === "todos" ? true : u.role === role))
      .map((u) => u.username)
      .filter(Boolean);
    setDestinatarios((prev) => [...new Set([...prev, ...emailsGrupo])]);
  };

  // Pide el HTML que de verdad recibiría cada destinatario (con la
  // plantilla activa en Personalizar, si hay una) en vez de mostrar el HTML
  // crudo de Quill — así lo que se ve acá coincide con lo que sale al pulsar
  // "Enviar".
  const abrirPreview = () => {
    setPreviewOpen(true);
    loadApi("admin/email_templates/preview", true, "post", {
      tipo: "boletin",
      contenidoPersonalizado: description || "<p><em>(sin contenido)</em></p>",
    })
      .then((res) => setPreviewHtml(res.data.html))
      .catch(() =>
        setPreviewHtml(description || "<p><em>(sin contenido)</em></p>")
      );
  };

  const agregarManual = () => {
    const email = manualEmail.trim().toLowerCase();
    if (!email) return;
    if (!isEmail(email)) {
      showSnack("Ese email no es válido", "warning");
      return;
    }
    setDestinatarios((prev) => [...new Set([...prev, email])]);
    setManualEmail("");
  };

  const handleSubmit = () => {
    if (destinatarios.length === 0) {
      showSnack("Selecciona al menos un destinatario", "warning");
      return;
    }
    if (!subject.trim()) {
      showSnack("Escribe un asunto", "warning");
      return;
    }
    if (!description.trim()) {
      showSnack("Escribe el contenido del correo", "warning");
      return;
    }

    loadApi("admin/correo", true, "post", {
      to: destinatarios,
      subject,
      description,
    })
      .then((res) => {
        const tieneFallidos = res.data.fallidos?.length > 0;
        showSnack(
          tieneFallidos
            ? `${res.data.message}. ${res.data.fallidos.length} correo(s) no se pudieron enviar.`
            : res.data.message,
          tieneFallidos ? "warning" : "success"
        );
        setSubject("");
        setDescription("");
        setDestinatarios([]);
      })
      .catch((e) => {
        showSnack(e.message, "error");
      });
  };

  return (
    <>
      <GlobalHeader />
      <Container>
        <Stack py={8} spacing={3}>
          <Typography variant="h4">Boletín de correos</Typography>
          <Typography variant="body2" color="grayText">
            Redacta el correo, elige a quién enviarlo (uno, varios, o un grupo
            completo) y se manda de forma independiente a cada destinatario.
          </Typography>

          <Stack spacing={1}>
            <Typography variant="subtitle2">Destinatarios</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="+ Todos los artistas"
                onClick={() => agregarGrupo("artista")}
                variant="outlined"
              />
              <Chip
                label="+ Todos los difusores"
                onClick={() => agregarGrupo("productor")}
                variant="outlined"
              />
              <Chip
                label="+ Todos los usuarios"
                onClick={() => agregarGrupo("todos")}
                variant="outlined"
                color="primary"
              />
            </Stack>
            <Autocomplete
              multiple
              options={opciones}
              value={destinatarios.map(
                (email) =>
                  opciones.find((o) => o.email === email) || {
                    email,
                    label: email,
                  }
              )}
              onChange={(e, value) =>
                setDestinatarios(value.map((v) => v.email))
              }
              isOptionEqualToValue={(option, value) =>
                option.email === value.email
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Buscar usuarios registrados"
                  placeholder="Nombre o email..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.email}
                    {...getTagProps({ index })}
                    key={option.email}
                  />
                ))
              }
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                label="Agregar un email escrito a mano"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    agregarManual();
                  }
                }}
                fullWidth
              />
              <LoadingButton variant="outlined" onClick={agregarManual}>
                Agregar
              </LoadingButton>
            </Stack>
            <Typography variant="caption" color="grayText">
              {destinatarios.length === 0
                ? "Ningún destinatario seleccionado"
                : `${destinatarios.length} destinatario(s) seleccionado(s)`}
            </Typography>
          </Stack>

          <TextField
            label="Asunto"
            type="text"
            variant="outlined"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Contenido del correo</Typography>
            <Box sx={{ ".ql-editor": { minHeight: "200px" } }}>
              <ReactQuill
                value={description}
                theme="snow"
                onChange={setDescription}
                modules={modules}
              />
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={abrirPreview}
              disabled={!subject.trim() && !description.trim()}
            >
              Vista previa
            </Button>
            <LoadingButton
              sx={{ py: 1, px: 5 }}
              endIcon={<SendIcon />}
              loading={loadingApi.includes("post__admin/correo")}
              onClick={handleSubmit}
            >
              Enviar
            </LoadingButton>
          </Stack>
        </Stack>
      </Container>

      {/* Vista previa: exactamente el HTML que recibirá cada destinatario,
          incluido el marco de la plantilla de correo activa en Personalizar
          (banner, logo, colores, pie) si hay una. Se usa un iframe en vez
          de dangerouslySetInnerHTML porque ese marco trae su propio fondo
          de página y de tarjeta — un iframe los aísla por completo de los
          estilos del panel de administración. */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Vista previa del correo</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Stack>
              <Typography variant="caption" color="grayText">
                De
              </Typography>
              <Typography variant="body2">Radar Freim</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="grayText">
                Asunto
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {subject || "(sin asunto)"}
              </Typography>
            </Stack>
            <Divider />
            <Box
              sx={{
                border: "1px solid #eee",
                borderRadius: 1,
                overflow: "hidden",
                height: "60vh",
              }}
            >
              <iframe
                title="Vista previa del boletín"
                srcDoc={previewHtml}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
};

export default BoletinAdmin;
